import React from 'react';

import { Flex, Paper, ScrollArea, ScrollAreaProps, Space, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { FullScreenLoader } from 'components/FullScreenLoader';
import { NotificationService } from 'services/notification-service';
import { ShopifyService } from 'services/shopify-service';
import { SHOPIFY_STORE_WEBSITE_QUERY_KEY } from 'shared/constants/query-keys';

interface StoreWebsiteDetailsProps extends ScrollAreaProps {
  storeId: string;
  isStoreConfigured: boolean;
}

export function StoreWebsiteDetails({ storeId, isStoreConfigured, ...scrollAreaProps }: StoreWebsiteDetailsProps) {
  const getStoreWebsite = async () => {
    if (isStoreConfigured) {
      const { website } = await ShopifyService.getEmbeddedStoreData(storeId);
      return website;
    } else {
      return await ShopifyService.getStoreWebsite(storeId);
    }
  };

  const onStoreWebsiteQueryError = () => {
    NotificationService.showResult(false, 'FEED_BACK.SHOPIFY.FAILURE.WEBSITE');
  };

  const WEBSITE_QUERY_REFRESH_TIME = 1000 * 60 * 5; // 5 minutes
  const storeWebsiteQuery = useQuery({
    queryFn: getStoreWebsite,
    queryKey: [SHOPIFY_STORE_WEBSITE_QUERY_KEY, { shopifyStoreId: storeId, isStoreConfigured }],
    onError: onStoreWebsiteQueryError,
    staleTime: WEBSITE_QUERY_REFRESH_TIME
  });

  if (storeWebsiteQuery.isLoading) return <FullScreenLoader w="100%" h="100%" />;
  const storeUrls = storeWebsiteQuery.data
    ? isStoreConfigured
      ? storeWebsiteQuery.data.subLinks
      : [storeWebsiteQuery.data.url, ...storeWebsiteQuery.data.subLinks]
    : [];

  return (
    <ScrollArea offsetScrollbars w="100%" h="100%" {...scrollAreaProps}>
      {storeUrls.map((subLink, index: number) => {
        return (
          <Paper key={index} withBorder p={10} mt={index === 0 ? 0 : 7} mb={index === storeUrls.length - 1 ? 0 : 7}>
            <Flex direction="row" align="center">
              <Space w={10} />
              <Text>{subLink}</Text>
            </Flex>
          </Paper>
        );
      })}
    </ScrollArea>
  );
}
